import axios from "axios";
import React, { createContext, useContext, useEffect, useState } from "react";
import { Agency } from "../db/schema";
import { isAuthPage } from "../utilities/isAuthPage";

export type AgencyWithAdmin = Agency & {
  admin_id?: string;
  admin_email?: string;
  admin_name?: string | null;
  admin_seen_tutorials?: string[];
  admin_created_at?: string | Date | null;
  type?: "agency" | "agency_admin";
};

interface AgencyAuthContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  agency: AgencyWithAdmin | null;
  setAgency: React.Dispatch<React.SetStateAction<AgencyWithAdmin | null>>;
  loading: boolean;
  mfaRequired: boolean;
  mfaVerified: boolean;
  mfaConfigured: boolean;
  setMfaVerified: (verified: boolean) => void;
}

const AgencyAuthContext = createContext<AgencyAuthContextType | undefined>(
  undefined
);

export const AgencyAuthProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [agency, setAgency] = useState<AgencyWithAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(true);
  const [mfaConfigured, setMfaConfigured] = useState(false);

  useEffect(() => {
    const checkAuthStatus = async () => {
      // Skip auth check on auth pages
      if (isAuthPage) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get("/api/auth/agency");
        if (response.data.user && "max_users" in response.data.user) {
          setIsAuthenticated(true);
          setAgency(response.data.user);
          setMfaRequired(response.data.mfaRequired || false);
          setMfaVerified(response.data.mfaVerified ?? true);
          setMfaConfigured(response.data.mfaConfigured || false);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setAgency(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  return (
    <AgencyAuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        agency,
        setAgency,
        loading,
        mfaRequired,
        mfaVerified,
        mfaConfigured,
        setMfaVerified,
      }}
    >
      {children}
    </AgencyAuthContext.Provider>
  );
};

export const useAgencyAuth = () => {
  const context = useContext(AgencyAuthContext);
  if (context === undefined) {
    throw new Error("useAgencyAuth must be used within an AgencyAuthProvider");
  }
  return context;
};
